import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const J  = { fontFamily: "'Josefin Sans', sans-serif" };
const CG = { fontFamily: "'Libre Baskerville', Georgia, serif" };
const PAD   = 12;
const NAV_H = 80; // bottom-nav height + safe area — callout stays above this

export default function SpotlightTour({ steps, onDone }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect]       = useState(null);
  const [wh, setWh]           = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));

  // Measure the highlighted element whenever the step changes.
  // Scrolls it into view first so below-fold elements are visible before measuring.
  useEffect(() => {
    const step = steps[stepIdx];
    step?.onEnter?.();
    const delay = step?.onEnter ? 220 : 60;
    const t = setTimeout(() => {
      const el = step?.ref?.current;
      if (el) {
        el.scrollIntoView({ behavior: 'instant', block: 'center' });
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setRect(el.getBoundingClientRect());
      } else {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setRect(null);
      }
    }, delay);
    return () => clearTimeout(t);
  }, [stepIdx, steps]);

  // Re-measure on resize
  useEffect(() => {
    function onResize() {
      setWh({ w: window.innerWidth, h: window.innerHeight });
      const el = steps[stepIdx]?.ref?.current;
      if (el) setRect(el.getBoundingClientRect());
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [stepIdx, steps]);

  function advance() {
    if (stepIdx < steps.length - 1) setStepIdx(s => s + 1);
    else onDone();
  }

  function back() {
    setStepIdx(s => s - 1);
  }

  const step    = steps[stepIdx];
  const isLast  = stepIdx === steps.length - 1;
  const hasCta  = Boolean(step.ctaLabel);

  // Conservative height estimate — err tall so the overflow check errs toward
  // placing the callout above the element rather than below-and-clipped.
  const calloutH = hasCta ? 320 : 280;

  // Usable viewport height = total height minus the bottom nav bar
  const usableH = wh.h - NAV_H;

  const calloutW    = Math.min(280, wh.w - 32);
  const calloutLeft = rect
    ? Math.max(16, Math.min(rect.left + rect.width / 2 - calloutW / 2, wh.w - calloutW - 16))
    : (wh.w - calloutW) / 2;

  // Place callout above the highlighted element if it would overflow the usable area
  const calloutAbove = rect ? (rect.bottom + PAD + calloutH + 16 > usableH) : false;
  const rawTop = !rect
    ? usableH / 2 - calloutH / 2
    : calloutAbove
      ? Math.max(8, rect.top - PAD - calloutH - 10)
      : rect.bottom + PAD + 10;
  // Hard clamp: never let the callout bottom go below the usable area
  const calloutTop = Math.min(rawTop, usableH - calloutH - 8);

  // Portal to document.body so z-index is never constrained by an ancestor
  // stacking context — fixes nav bar appearing over the overlay when the tour
  // is triggered from deeply-nested components (e.g. inside AskAI.jsx card).
  return createPortal((
    <>
      {/* Dark overlay + backdrop — always above BottomNav (z 9210 > nav 9200) */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 9210 }}>
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          <defs>
            <mask id="spot-mask">
              <rect x={0} y={0} width={wh.w} height={wh.h} fill="white" />
              {rect && (
                <rect
                  x={rect.left - PAD} y={rect.top - PAD}
                  width={rect.width + PAD * 2} height={rect.height + PAD * 2}
                  rx={10} fill="black"
                />
              )}
            </mask>
          </defs>
          <rect x={0} y={0} width={wh.w} height={wh.h} fill="rgba(0,0,0,0.72)" mask="url(#spot-mask)" />
          {rect && (
            <rect
              x={rect.left - PAD} y={rect.top - PAD}
              width={rect.width + PAD * 2} height={rect.height + PAD * 2}
              rx={10} fill="none" stroke="rgba(229,150,83,0.65)" strokeWidth={1.5}
            />
          )}
        </svg>
        {/* Tap backdrop to dismiss */}
        <div style={{ position: 'absolute', inset: 0 }} onClick={onDone} />
      </div>

      {/* Callout bubble — sits ABOVE the BottomNav (z 9250 > nav 9200)
          so it is never hidden behind the nav bar */}
      <div
        style={{
          position: 'fixed',
          left: calloutLeft,
          top: calloutTop,
          width: calloutW,
          background: 'var(--bg)',
          borderRadius: 12,
          padding: '1rem 1.1rem',
          border: `1px solid rgba(229,150,83,${hasCta ? '0.6' : '0.3'})`,
          boxShadow: hasCta ? '0 4px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(229,150,83,0.15)' : '0 4px 32px rgba(0,0,0,0.5)',
          animation: 'fadeUp 0.22s ease',
          zIndex: 9250,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Step progress dots */}
        <div style={{ display: 'flex', gap: 4, marginBottom: '0.7rem' }}>
          {steps.map((_, i) => (
            <div key={i} style={{
              height: 4, borderRadius: 2,
              width: i === stepIdx ? 16 : 4,
              background: i === stepIdx ? 'var(--gold)' : i < stepIdx ? 'rgba(229,150,83,0.4)' : 'var(--border)',
              transition: 'all 0.22s',
            }} />
          ))}
        </div>

        <div style={{ ...CG, fontSize: '0.92rem', fontWeight: 500, color: 'var(--text)', lineHeight: 1.3, marginBottom: '0.3rem' }}>
          {step.title}
        </div>
        <div style={{ ...CG, fontSize: '0.8rem', color: 'var(--text2)', lineHeight: 1.65, marginBottom: '0.85rem' }}>
          {step.desc}
        </div>

        {/* Navigation buttons: Back replaces Skip after step 0 */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: hasCta ? '0.5rem' : 0 }}>
          {stepIdx === 0 ? (
            <button
              onClick={onDone}
              style={{
                ...J, flex: 1, padding: '0.55rem',
                background: 'transparent', border: '1px solid var(--border)',
                borderRadius: 6, fontSize: '0.54rem', letterSpacing: '0.12em',
                textTransform: 'uppercase', color: 'var(--text3)', cursor: 'pointer',
              }}
            >Skip</button>
          ) : (
            <button
              onClick={back}
              style={{
                ...J, flex: 1, padding: '0.55rem',
                background: 'transparent', border: '1px solid var(--border)',
                borderRadius: 6, fontSize: '0.54rem', letterSpacing: '0.12em',
                textTransform: 'uppercase', color: 'var(--text3)', cursor: 'pointer',
              }}
            >← Back</button>
          )}
          <button
            onClick={advance}
            style={{
              ...J, flex: 2, padding: '0.55rem',
              background: 'var(--gold)', border: 'none',
              borderRadius: 6, fontSize: '0.54rem', letterSpacing: '0.12em',
              textTransform: 'uppercase', color: '#1a1409', cursor: 'pointer', fontWeight: 700,
            }}
          >{isLast ? 'Done' : `Next  ${stepIdx + 1} / ${steps.length}`}</button>
        </div>

        {/* Optional CTA button — for steps that warrant a direct action */}
        {hasCta && (
          <button
            onClick={() => { step.ctaAction?.(); onDone(); }}
            style={{
              ...J, width: '100%', padding: '0.65rem',
              background: 'rgba(229,150,83,0.15)', border: '1px solid rgba(229,150,83,0.5)',
              borderRadius: 6, fontSize: '0.56rem', letterSpacing: '0.14em',
              textTransform: 'uppercase', color: 'var(--gold)', cursor: 'pointer', fontWeight: 600,
            }}
          >{step.ctaLabel}</button>
        )}
      </div>
    </>
  ), document.body);
}
